import React from 'react';
import { Card, Text, InlineStack, BlockStack, Icon } from '@shopify/polaris';
import { ArrowUpMinor, ArrowDownMinor, MinusMinor } from '@shopify/polaris-icons';

/**
 * KPI Card Component
 * Displays a key performance indicator with value, trend, and change percentage
 */
const KPICard = ({
    title,
    value,
    change = null,
    trend = null, // 'up', 'down', or null
    format = 'currency', // 'currency', 'number', 'percentage'
    subtitle = null,
    loading = false
}) => {
    const formatValue = (val) => {
        if (val === null || val === undefined) return '-';

        switch (format) {
            case 'currency':
                return new Intl.NumberFormat('en-EG', {
                    style: 'currency',
                    currency: 'EGP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(val);
            case 'percentage':
                return `${val.toFixed(1)}%`;
            case 'number':
                return new Intl.NumberFormat('en-EG').format(val);
            default:
                return val;
        }
    };

    const getTrendIcon = () => {
        if (trend === 'up') return ArrowUpMinor;
        if (trend === 'down') return ArrowDownMinor;
        return MinusMinor;
    };

    const getTrendColor = () => {
        if (trend === 'up') return 'success';
        if (trend === 'down') return 'critical';
        return 'subdued';
    };

    const formatChange = () => {
        if (change === null || change === undefined) return null;
        const prefix = change > 0 ? '+' : '';
        return `${prefix}${change.toFixed(1)}%`;
    };

    if (loading) {
        return (
            <Card>
                <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" tone="subdued">{title}</Text>
                    <div style={{ height: '32px', background: '#f1f1f1', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
                </BlockStack>
            </Card>
        );
    }

    return (
        <Card>
            <BlockStack gap="200">
                <Text as="p" variant="bodyMd" tone="subdued">{title}</Text>
                <InlineStack align="space-between" blockAlign="end">
                    <Text as="p" variant="headingLg" fontWeight="bold">
                        {formatValue(value)}
                    </Text>
                    {change !== null && (
                        <InlineStack gap="100" blockAlign="center">
                            <Icon source={getTrendIcon()} tone={getTrendColor()} />
                            <Text as="span" variant="bodySm" tone={getTrendColor()}>
                                {formatChange()}
                            </Text>
                        </InlineStack>
                    )}
                </InlineStack>
                {subtitle && (
                    <Text as="p" variant="bodySm" tone="subdued">{subtitle}</Text>
                )}
            </BlockStack>
        </Card>
    );
};

export default KPICard;
